import {
  BLANK_VALUE,
  IN_FILTER_FUNCTION,
  IS_FALSE_FILTER_FUNCTION,
  IS_TRUE_FILTER_FUNCTION,
  ISO_DATE_PATTERN,
  LOWER_BOUND_FILTER_FUNCTION,
  LOWER_BOUND_FILTER_FUNCTIONS,
  UPPER_BOUND_FILTER_FUNCTION,
  UPPER_BOUND_FILTER_FUNCTIONS,
} from "../types/constants";
import { reportsV3DynamicDateRangeFunction } from "config";

// The filter-spec vocabulary shared by both places a report's filters are
// built: the results grid (filters) and the builder (preAggregationFilters).
// Date parameters are written as yyyy-mm-dd; parsing also accepts the full ISO
// datetimes that earlier saved specs carry. A dynamic date range is a single
// spec whose parameters are its start expression, end expression and display
// name.

export function formatDateInput(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// A bare yyyy-mm-dd is read at local midnight rather than UTC midnight, so a
// day does not shift for users west of Greenwich.
export function parseDateInput(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = ISO_DATE_PATTERN.test(trimmed)
    ? new Date(`${trimmed}T00:00:00`)
    : new Date(trimmed);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

// Toggles a filter panel option. The panel collapses options that share a
// display label into one row, so it reports every id behind the row rather
// than a single value.
export function toggleSelectedValues(
  selectedValues,
  valueOrIds,
  isBinary = false
) {
  const ids = Array.isArray(valueOrIds) ? valueOrIds : [valueOrIds];
  if (isBinary) {
    const value = ids[0];
    return selectedValues.has(value) ? new Set() : new Set([value]);
  }
  const next = new Set(selectedValues);
  const allSelected = ids.every((id) => next.has(id));
  ids.forEach((id) => {
    if (allSelected) next.delete(id);
    else next.add(id);
  });
  return next;
}

export function buildValueFilterSpec(column, values, isBinary = false) {
  const parameters = [...values].map((v) => (v === BLANK_VALUE ? "" : v));
  if (isBinary) {
    return {
      column,
      function:
        parameters[0] === "true"
          ? IS_TRUE_FILTER_FUNCTION
          : IS_FALSE_FILTER_FUNCTION,
      parameters: [],
    };
  }
  return { column, function: IN_FILTER_FUNCTION, parameters };
}

// A bound is a Date when it comes from a filter panel, or an already-encoded
// string when it comes from company config, which is passed through as written.
const toDateParameter = (bound) =>
  typeof bound === "string" ? bound : formatDateInput(bound);

export function buildDateFilterSpecs(column, range) {
  if (range?.dynamic) {
    const [start, end] = range.dynamic.parameters;
    return [
      {
        column,
        function: reportsV3DynamicDateRangeFunction(),
        parameters: [start, end, range.dynamic.name],
      },
    ];
  }
  const specs = [];
  if (range?.from) {
    specs.push({
      column,
      function: LOWER_BOUND_FILTER_FUNCTION,
      parameters: [toDateParameter(range.from)],
    });
  }
  if (range?.to) {
    specs.push({
      column,
      function: UPPER_BOUND_FILTER_FUNCTION,
      parameters: [toDateParameter(range.to)],
    });
  }
  return specs;
}

export function buildNumberFilterSpecs(column, range) {
  const specs = [];
  if (range?.min !== null && range?.min !== undefined) {
    specs.push({
      column,
      function: LOWER_BOUND_FILTER_FUNCTION,
      parameters: [range.min],
    });
  }
  if (range?.max !== null && range?.max !== undefined) {
    specs.push({
      column,
      function: UPPER_BOUND_FILTER_FUNCTION,
      parameters: [range.max],
    });
  }
  return specs;
}

// Reads filter specs back into the value / date / number maps the panels edit.
// getColumnFilterType resolves a column to "value", "date" or "number"; when it
// is absent or cannot answer, the parameter's own type decides, which is what
// the builder relies on since it reads a saved spec before column types are
// known. A dynamic date spec is always read as a date range.
export function deriveFilterMaps(filters, getColumnFilterType) {
  const value = {};
  const date = {};
  const number = {};

  for (const filter of filters ?? []) {
    const { column } = filter;

    if (filter.function === IN_FILTER_FUNCTION) {
      value[column] = new Set((filter.parameters ?? []).map(String));
      continue;
    }
    if (filter.function === IS_TRUE_FILTER_FUNCTION) {
      value[column] = new Set(["true"]);
      continue;
    }
    if (filter.function === IS_FALSE_FILTER_FUNCTION) {
      value[column] = new Set(["false"]);
      continue;
    }

    if (filter.function === reportsV3DynamicDateRangeFunction()) {
      const parameters = filter.parameters ?? [];
      if (parameters.length < 2) continue;
      const [start, end, name] = parameters;
      date[column] = {
        from: null,
        to: null,
        dynamic: {
          parameters: [start, end],
          name: name || `${start} \u2013 ${end}`,
        },
      };
      continue;
    }

    const isLowerBound = LOWER_BOUND_FILTER_FUNCTIONS.includes(filter.function);
    const isUpperBound = UPPER_BOUND_FILTER_FUNCTIONS.includes(filter.function);
    if (!isLowerBound && !isUpperBound) continue;

    const parameter = filter.parameters?.[0];
    const filterType =
      getColumnFilterType?.(column) ??
      (typeof parameter === "string" ? "date" : "number");

    if (filterType === "date") {
      const parsed = parseDateInput(parameter);
      if (!parsed) continue;
      const existing = date[column] ?? { from: null, to: null };
      date[column] = isLowerBound
        ? { ...existing, from: parsed }
        : { ...existing, to: parsed };
    } else {
      const numeric =
        typeof parameter === "number" ? parameter : Number(parameter);
      if (Number.isNaN(numeric)) continue;
      const existing = number[column] ?? { min: null, max: null };
      number[column] = isLowerBound
        ? { ...existing, min: numeric }
        : { ...existing, max: numeric };
    }
  }

  return { value, date, number };
}
